import React, { useEffect, useState } from 'react';
import toastr from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Rating,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BackIcon } from 'components/SVG';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import MemoryHeaderSkeleton from 'sharedComponents/Skeleton/MemoryHeaderSkeleton';
import SparkBadges from 'components/common/SparkBadges';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import {
  useCreateFavouriteList,
  useCreateMemoryFavourite,
  useDeleteMemoryFavourite,
} from 'hooks/Favourites';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import {
  useCurrentUser,
  useDialog,
  useMemoryEditContext,
  useResize,
} from 'hooks/common';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import { getReviewText } from 'utilities/index';
import { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { SPARK_BADGE_TYPES } from 'constants/common';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import { useUpdateMemory } from 'hooks/memory/useUpdateMemory';
import { useAddMemoryRating, useUpdateMemoryRating } from 'hooks/memory';
import MemoryCardModal from '../Modals/MemoryCardModal';

function MemoryEditHeader() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const callbackURL = searchParams.get('callbackURL');
  const { memoryID } = useMemoryEditContext();
  const { currentUserID } = useCurrentUser();
  const { mutateAsync: updateMemory } = useUpdateMemory(memoryID);
  const infoParam = searchParams.get('info');

  const { joinedMemory, isLoading } = useMemoryByID(memoryID);
  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();
  const {
    mutateAsync: createMemoryFavorites,
    isLoading: isCreatingMemoryFavourite,
  } = useCreateMemoryFavourite(memoryID);
  const [isDeletingMemory, setIsDeletingMemory] = useState(false);
  const [isArchivingMemory, setIsArchivingMemory] = useState(false);
  const { mutateAsync: addMemoryRating } = useAddMemoryRating(memoryID);

  const { ratings, permissions } = joinedMemory?.meta.user || {};
  const allowedToReview = permissions?.canReview;
  const userRating = ratings?.find((rating) => rating.memoryID === memoryID);

  const { mutateAsync: updateMemoryRating } = useUpdateMemoryRating(
    userRating?.id ? `${userRating?.id}` : undefined,
    memoryID
  );

  const {
    mutateAsync: deleteMemoryFavorites,
    isLoading: isDeletingMemoryFavourite,
  } = useDeleteMemoryFavourite(memoryID);

  const existingFavouriteList = joinedMemory?.meta.user.favouriteLists;
  const memoryFavourites = joinedMemory?.memoryFavourite;

  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    });
  };

  const handleFavouriteListItem = async (listID: number) => {
    const foundExistingFavouriteList = memoryFavourites?.find(
      (favourite) => favourite.attributes.favouriteListID === listID
    );
    if (foundExistingFavouriteList) {
      await deleteMemoryFavorites(`${foundExistingFavouriteList.id}`);
      return;
    }
    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: listID,
    });
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      memoryID: joinedMemory?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };

  const isLoadingMemoryFavourites =
    isCreatingMemoryFavourite || isDeletingMemoryFavourite;

  const { isMobile } = useResize();
  const handleBackClick = () => {
    if (callbackURL) {
      navigate(callbackURL, { state: { selectedTabIndex: 1 } });
      return;
    }
    navigate('/dashboard');
  };
  const badgesCategoryMap = getGropedSparkBadges(joinedMemory?.meta?.badges);

  const handleDeleteMemory = async () => {
    setIsDeletingMemory(true);
    try {
      await updateMemory({
        isDeleted: true,
      });
      toastr.success('Knowledge Source deleted successfully.');
    } catch (error) {
      toastr.error('Failed to delete the Knowledge Source. Please try again.');
    } finally {
      setIsDeletingMemory(false);
      setTimeout(() => {
        navigate('/dashboard');
      }, 3500);
    }
  };
  const handleArchiveMemory = async () => {
    setIsArchivingMemory(true);
    try {
      await updateMemory({
        isArchived: true,
      });
    } catch (error) {
      toastr.error('Failed to archive the Knowledge Source. Please try again.');
    } finally {
      setTimeout(() => {
        setIsArchivingMemory(false);
        navigate('/dashboard');
      }, 3500);
    }
  };

  const handleUpdateMemoryRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addMemoryRating({ ...values, memoryID });
    }
    if (doesReviewExists) {
      await updateMemoryRating({ ...values, memoryID });
    }
  };

  const {
    openDialog: openMemoryCardDialog,
    closeDialog: closeMemoryCardDialog,
    open: isMemoryCardDialogOpen,
  } = useDialog();

  useEffect(() => {
    if (infoParam === '1') {
      openMemoryCardDialog();
    }
  }, [infoParam, openMemoryCardDialog]);

  const userPermissions = joinedMemory?.meta.user.permissions;

  return (
    <AppBar>
      <Grid
        container
        item
        justifyContent='space-between'
        position='fixed'
        bgcolor={(theme) => theme.palette.common.white}
        height='6rem'
        paddingX={2}
        borderBottom={(theme) => `1px solid ${theme.palette.borderColor}`}
        wrap='nowrap'
      >
        <Box display='flex' justifyContent='center' alignItems='center'>
          {currentUserID && !isMobile && (
            <Button
              onClick={handleBackClick}
              sx={{
                minWidth: 'unset',
                width: '45px',
                height: '45px',
                padding: 0,
                borderWidth: '1px',
                borderRadius: '14px',
                borderColor: '#EEEEEE',
                aspectRatio: 1,
              }}
              variant='outlined'
            >
              <BackIcon />
            </Button>
          )}
          {isLoading ? (
            <MemoryHeaderSkeleton />
          ) : (
            <>
              <Box sx={{ display: 'flex', ml: !!currentUserID ? 2 : 0 }}>
                <SparkAvatarBackground
                  src={joinedMemory?.attributes?.iconURL || ''}
                  rounded={false}
                  icon={<SparkdefaultIcon width={'72px'} height={'72px'} />}
                  width='72px'
                  height='72px'
                />
              </Box>
              <Box paddingLeft={1.5} display='flex' flexDirection='column'>
                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Typography
                    variant='body1'
                    color={(theme) => theme.palette.common.black}
                  >
                    {joinedMemory?.attributes.name}
                  </Typography>
                  <Tooltip
                    title={'Click to learn more'}
                    placement='top'
                    arrow
                    enterTouchDelay={0}
                    onClick={openMemoryCardDialog}
                    style={{ cursor: 'pointer' }}
                  >
                    <InfoOutlinedIcon
                      fontSize='small'
                      sx={{ color: 'var(--color-label)' }}
                    />
                  </Tooltip>
                </Box>
                <Box display='flex' alignItems='center' gap={1}>
                  <Box>
                    <SparkBadges
                      badges={
                        badgesCategoryMap?.[SPARK_BADGE_TYPES.Creator] || []
                      }
                    />
                  </Box>
                  <Rating
                    readOnly
                    value={joinedMemory?.meta.averageRating ?? 1}
                    precision={0.1}
                    max={1}
                  />
                  <Typography variant='body2' color='black' paddingTop={0.5}>
                    {getReviewText(joinedMemory?.meta.numberOfRatings)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
        <Grid
          container
          gap={1}
          justifyContent='flex-end'
          alignItems='center'
          width='50%'
        >
          {(isDeletingMemory ||
            isArchivingMemory ||
            isLoadingMemoryFavourites) && (
            <CircularProgress
              variant='indeterminate'
              thickness={8}
              sx={{
                width: `30px !important`,
                height: `30px !important`,
              }}
            />
          )}
          <SparkOptionsModal
            resourceLogoURL={joinedMemory?.attributes.iconURL || ''}
            options={joinedMemory?.meta.options}
            resourceID={joinedMemory?.id}
            privacyTypeId={joinedMemory?.attributes.memoryPrivacyTypeID}
            resourceAccessLinkToken={joinedMemory?.attributes.accessLinkToken}
            handleSparkRatingSave={handleUpdateMemoryRating}
            userRating={userRating}
            allowedToReview={allowedToReview}
            resourceType='memory'
            onFavouriteItemClick={handleFavouriteListItem}
            existingFavouriteList={existingFavouriteList || []}
            onCreateList={handleCreateList}
            flagReasonsDropdownOptions={flagReasonsDropdownOptions}
            onSendReport={handleSendReport}
            isResourceFavourite={joinedMemory?.meta.user.isFavourited}
            isLoading={isLoadingMemoryFavourites}
            resourceName={joinedMemory?.attributes.name || '- -'}
            onDelete={handleDeleteMemory}
            onArchive={handleArchiveMemory}
            showDotsIcon={!!currentUserID}
            userPermissions={userPermissions}
            showHeartIcon={!!currentUserID}
          />
        </Grid>
        {isMemoryCardDialogOpen && !!joinedMemory && (
          <MemoryCardModal
            isMemoryCardDialogOpen={isMemoryCardDialogOpen}
            closeMemoryCardDialog={closeMemoryCardDialog}
            memoryID={joinedMemory.id}
          />
        )}
      </Grid>
    </AppBar>
  );
}

export default MemoryEditHeader;
